import React, { useRef, useState, useEffect } from "react";
import { FaEllipsisVertical } from "react-icons/fa6";
import { PrimaryButton } from "@/components/Buttons.jsx";
import { useNavigate } from "react-router-dom";
import { getCrmBroadcasts } from "@/api/crm";
import { updateCrmBroadcast } from "@/api/crm";
import { getCrmBroadcast } from "@/api/crm";
import { duplicateCrmBroadcast } from "@/api/crm";
import { useDispatch, useSelector } from "react-redux";
import { addToast } from "@/store/app/action.js";
import CRMAccessBlocker from "@/partials/CRMAccessBlocker.jsx";
import DuplicateBroadcastModal from "@/components/DuplicateBroadcastModal.jsx";
import InlineLoader from "@/components/InlineLoader.jsx";

const statusColor = (status) => {
  if (status === "sent") return "bg-green-100 text-green-700";
  if (status === "scheduled") return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-700";
};

const formatStatusText = (status, publishUp, dateAdded, isPublished) => {
  let statusText = "";

  if (!isPublished) {
    return "Draft";
  } else if (!publishUp) {
    statusText = "Sent ASAP when created";
  } else {
    const date = new Date(publishUp);
    const timeString = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const dateString = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    if (status === "sent") {
      statusText = `Sent at ${timeString} on ${dateString}`;
    } else if (status === "scheduled") {
      statusText = `Scheduled to send at ${timeString} on ${dateString}`;
    }
  }

  // Add dateAdded if available
  if (dateAdded) {
    const addedDate = new Date(dateAdded);
    const addedDateString = addedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return (
      <>
        <div>{statusText}</div>
        <div className="text-xs text-slate-400 dark:text-slate-500">
          Created {addedDateString}
        </div>
      </>
    );
  }

  return statusText;
};

const BroadcastContextMenu = ({
  show,
  setShow,
  status,
  isPublished,
  onEdit,
  onPauseResume,
  onArchive,
  onSendTest,
  onDuplicate,
  anchorRef,
  buttonRef,
}) => {
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // Calculate position when menu opens
  useEffect(() => {
    if (show && buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const menuHeight = 120; // Approximate height of the menu
      
      // Check if menu would overflow bottom of viewport
      const wouldOverflowBottom = buttonRect.bottom + menuHeight > viewportHeight;
      
      setPosition({
        top: wouldOverflowBottom ? buttonRect.top - menuHeight : buttonRect.bottom,
        right: window.innerWidth - buttonRect.right
      });
    }
  }, [show, buttonRef]);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target) && 
          buttonRef?.current && !buttonRef.current.contains(event.target)) {
        setShow(false);
      }
    };
    if (show) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [show, setShow, anchorRef, buttonRef]);

  const primaryLabel = !isPublished ? "Edit" : (status === "sent" ? "View" : "Edit");

  if (!show) return null;

  return (
    <div
      ref={anchorRef}
      className="fixed w-48 py-2 z-50 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`
      }}
    >
      <span
        onClick={() => {
          setShow(false);
          onEdit();
        }}
        className="flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-900 text-slate-800 dark:text-slate-100 cursor-pointer"
      >
        {primaryLabel}
      </span>
      <span
        onClick={() => {
          setShow(false);
          onSendTest();
        }}
        className="flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-900 text-slate-800 dark:text-slate-100 cursor-pointer"
      >
        Send Test
      </span>
      <span
        onClick={() => {
          setShow(false);
          onDuplicate();
        }}
        className="flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-900 text-slate-800 dark:text-slate-100 cursor-pointer"
      >
        Duplicate
      </span>
      <span
        onClick={() => {
          setShow(false);
          onArchive();
        }}
        className="flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-900 text-red-500 dark:text-red-500 cursor-pointer"
      >
        Archive
      </span>
    </div>
  );
};

const CrmBroadcasts = () => {
  const [contextMenuIdx, setContextMenuIdx] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateBroadcastId, setDuplicateBroadcastId] = useState(null);
  const [duplicateBroadcastName, setDuplicateBroadcastName] = useState("");
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const contextRefs = useRef([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const crmSettings = useSelector((state) => state.crmSettings);

  // Fetch broadcasts from API
  useEffect(() => {
    const fetchBroadcasts = async () => {
      try {
        setLoading(true);
        const response = await getCrmBroadcasts();
        if (response.status === "success") {
          setBroadcasts(response.emails || []);
        }
      } catch (error) {
        // Fallback to mock data if API fails
      } finally {
        setLoading(false);
      }
    };

    fetchBroadcasts();
  }, []);

  // Helper function to format numbers with k suffix
  const formatNumber = (num) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}k`;
    }
    return num.toString();
  };

  // Helper function to determine status
  const getStatus = (publishUp) => {
    const now = new Date();
    const publishDate = new Date(publishUp);
    return publishDate > now ? "scheduled" : "sent";
  };

  // Handlers for context menu actions (mock)
  const handleEdit = (idx) => navigate(`/crm/broadcasts/${broadcasts[idx].id}`);
  const handleSendTest = (idx) => navigate(`/crm/broadcasts/${broadcasts[idx].id}?openTest=1`);
  const handlePauseResume = (idx) => {
    if (crmSettings.development_mode) {
      dispatch(addToast({ 
        type: "warning", 
        message: "Publishing actions are disabled in development mode", 
        timer: 5 
      }));
      return;
    }
  };
  const handleArchive = async (idx) => {
    try {
      const id = broadcasts[idx].id;
      // Fetch full broadcast to build complete payload
      const detail = await getCrmBroadcast(id);
      if (detail.status !== "success") {
        throw new Error("Failed to load broadcast before archiving");
      }
      const emailData = detail.email || {};
      const mauticEmailData = detail.mautic_email || {};

      // Build full update payload + archived
      const scheduleDate = mauticEmailData.schedule || emailData.publishUp || "";
      const payload = {
        name: mauticEmailData.name || emailData.name || "",
        description: mauticEmailData.description || "",
        subject: mauticEmailData.subject || emailData.subject || "",
        html: mauticEmailData.html || "",
        exclude_header: mauticEmailData.exclude_header || false,
        is_published: mauticEmailData.is_published || false,
        archived: true,
      };
      if (mauticEmailData.design) payload.design = mauticEmailData.design;
      if (scheduleDate) payload.schedule = new Date(scheduleDate).toISOString();

      await updateCrmBroadcast(id, payload);
      dispatch(addToast({ type: "success", message: "Broadcast archived", timer: 5 }));
      // refetch list
      const response = await getCrmBroadcasts();
      if (response.status === "success") {
        setBroadcasts(response.emails || []);
      }
    } catch (e) {
      let errorMessage = "Failed to archive broadcast";
      
      // Try to parse the error response to get the server's message
      if (e?.response) {
        try {
          const responseData = e.response?.data
          if (responseData?.message) {
            errorMessage = responseData.message;
          }
        } catch (parseError) {
        }
      }
      
      dispatch(addToast({ type: "error", message: errorMessage, timer: 8 }));
    }
  };

  const handleDuplicate = (idx) => {
    const broadcast = broadcasts[idx];
    setDuplicateBroadcastId(broadcast.id);
    setDuplicateBroadcastName(broadcast.name);
    setDuplicateModalOpen(true);
  };

  const handleDuplicateConfirm = async (newName) => {
    try {
      setDuplicateLoading(true);
      const response = await duplicateCrmBroadcast(duplicateBroadcastId, newName);
      
      if (response.status === "success") {
        dispatch(addToast({ 
          type: "success", 
          message: `Broadcast "${newName}" created successfully`, 
          timer: 5 
        }));
        
        // Get the new broadcast ID from the response
        const newBroadcastId = response.email?.id || response.id;
        
        if (newBroadcastId) {
          // Navigate to the broadcast form with the new duplicated broadcast
          navigate(`/crm/broadcasts/${newBroadcastId}`);
        } else {
          // Fallback: refresh the broadcasts list if ID not found
          const broadcastsResponse = await getCrmBroadcasts();
          if (broadcastsResponse.status === "success") {
            setBroadcasts(broadcastsResponse.emails || []);
          }
          setDuplicateModalOpen(false);
        }
      } else {
        throw new Error(response.message || "Failed to duplicate broadcast");
      }
    } catch (e) {
      let errorMessage = "Failed to duplicate broadcast";
      
      // Try to parse the error response to get the server's message
      if (e?.response) {
        try {
          const responseData = e.response?.data
          if (responseData?.message) {
            errorMessage = responseData.message;
          }
        } catch (parseError) {
        }
      }
      
      dispatch(addToast({ type: "error", message: errorMessage, timer: 8 }));
      setDuplicateModalOpen(false);
    } finally {
      setDuplicateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <InlineLoader message="Loading broadcasts..." minHeight="h-64" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        {/* Left: Title */}
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            CRM Broadcasts
          </h1>
        </div>
        {/* Right: Actions */}
        <div>
          <PrimaryButton onClick={() => navigate("/crm/broadcasts/new")}>
            New Broadcast
          </PrimaryButton>
        </div>
      </div>
      
      {/* Development Mode Warning */}
      {crmSettings.development_mode && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-yellow-800 dark:text-yellow-200 font-medium">
              Development Mode Active
            </span>
          </div>
          <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
            Publishing broadcasts is disabled while development mode is enabled. You can still create and edit drafts.
          </p>
        </div>
      )}
      
      {/* Broadcasts Table */}
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="font-semibold text-slate-800 dark:text-slate-100">
            {broadcasts.length} Broadcasts
          </div>
          {/* Add filters/date pickers here if needed */}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-900/20">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Read/Sent</th>
                <th className="px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((c, i) => {
                const status = getStatus(c.publishUp);
                const readPercentage =
                  c.sentCount > 0 ? (c.readCount / c.sentCount) * 100 : 0;
                return (
                  <tr
                    key={c.id}
                    className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/10 relative"
                  >
                    <td className="px-4 py-3 whitespace-nowrap font-medium">
                      <div className="flex flex-col">
                        <span>{c.name}</span>
                        <div className="text-xs text-slate-500 dark:text-slate-400 font-normal">
                          {formatStatusText(
                            status,
                            c.publishUp,
                            c.dateAdded,
                            c.isPublished
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {!c.isPublished ? (
                        <span className="inline-block px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                          Draft
                        </span>
                      ) : (
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-semibold ${statusColor(
                            status
                          )} capitalize`}
                        >
                          {status}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {status === "sent" ? (
                        <>
                          <div className="font-mono">
                            {formatNumber(c.readCount)}/
                            {formatNumber(c.sentCount)}
                          </div>
                          <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded mt-1">
                            <div
                              className="h-4 bg-blue-400 rounded"
                              style={{ width: `${readPercentage}%` }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="text-slate-400 dark:text-slate-500 text-sm">
                          Not sent yet
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right relative">
                      <button
                        className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setContextMenuIdx(contextMenuIdx === i ? null : i);
                        }}
                        ref={(el) => (contextRefs.current[i] = el)}
                      >
                        <FaEllipsisVertical />
                      </button>
                      <BroadcastContextMenu
                        show={contextMenuIdx === i}
                        setShow={(v) => setContextMenuIdx(v ? i : null)}
                        status={status}
                        isPublished={c.isPublished}
                        onEdit={() => handleEdit(i)}
                        onPauseResume={() => handlePauseResume(i)}
                        onArchive={() => handleArchive(i)}
                        onSendTest={() => handleSendTest(i)}
                        onDuplicate={() => handleDuplicate(i)}
                        anchorRef={{ current: contextRefs.current[i] }}
                        buttonRef={{ current: contextRefs.current[i] }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <CRMAccessBlocker />
      
      {/* Duplicate Broadcast Modal */}
      <DuplicateBroadcastModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        onConfirm={handleDuplicateConfirm}
        originalName={duplicateBroadcastName}
        isLoading={duplicateLoading}
      />
    </div>
  );
};

export default CrmBroadcasts;
